import { useState } from 'react';

const DEFAULT_BRANDING = {
  clientLogo: '',
  clientName: '',
  companyName: '',
  showNEGLogo: true, // "Powered by Next Experience Group"
  source: 'negroup', // 'negroup' (default), 'company', 'audit'
};

export default function WhiteLabelSettings({ audit, onUpdate }) {
  const branding = audit.branding || DEFAULT_BRANDING;
  const [previewLogo, setPreviewLogo] = useState(branding.clientLogo || '');

  const updateBranding = (field, value) => {
    onUpdate({ ...audit, branding: { ...branding, [field]: value } });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewLogo(reader.result);
      updateBranding('clientLogo', reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="card p-5">
      <h3 className="text-sm font-semibold text-white mb-1">White Label Settings</h3>
      <p className="text-[10px] text-neutral-500 mb-4">Customize branding for the client-facing report</p>

      {/* Source toggle */}
      <div className="flex gap-2 mb-4">
        {[
          { id: 'negroup', label: 'NEGroup Default' },
          { id: 'company', label: 'Company Brand' },
          { id: 'audit', label: 'Custom (This Audit)' },
        ].map(opt => (
          <button
            key={opt.id}
            onClick={() => updateBranding('source', opt.id)}
            className={`text-[10px] px-3 py-1.5 rounded-lg transition-all ${
              branding.source === opt.id
                ? 'bg-brand-gold/20 text-brand-gold border border-brand-gold/40'
                : 'bg-neutral-800/40 text-neutral-500 border border-neutral-800 hover:text-neutral-300'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {branding.source !== 'negroup' && (
        <div className="space-y-3">
          {/* Client Logo */}
          <div>
            <label className="label-text text-xs">Client Logo</label>
            <div className="flex items-center gap-3">
              {previewLogo ? (
                <div className="flex items-center gap-2">
                  <img src={previewLogo} alt="Logo" className="h-10 w-auto max-w-[120px] object-contain bg-white/10 rounded p-1" />
                  <button onClick={() => { setPreviewLogo(''); updateBranding('clientLogo', ''); }} className="text-[9px] text-neutral-600 hover:text-red-400">Remove</button>
                </div>
              ) : (
                <label className="cursor-pointer text-[10px] px-3 py-2 rounded bg-neutral-800/50 text-neutral-500 hover:text-neutral-300 border border-dashed border-neutral-700 transition-colors">
                  Upload Logo
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                </label>
              )}
              <span className="text-[9px] text-neutral-700">or</span>
              <input
                type="text"
                value={branding.clientLogo?.startsWith('data:') ? '' : (branding.clientLogo || '')}
                onChange={e => { setPreviewLogo(e.target.value); updateBranding('clientLogo', e.target.value); }}
                placeholder="Paste logo URL..."
                className="input-field text-xs py-1.5 flex-1"
              />
            </div>
          </div>

          {/* Client Name */}
          <div>
            <label className="label-text text-xs">Client Name (shown in report header)</label>
            <input
              type="text"
              value={branding.clientName || ''}
              onChange={e => updateBranding('clientName', e.target.value)}
              placeholder="e.g., The Franklin Bar"
              className="input-field text-sm py-2"
            />
          </div>

          {/* Company Name */}
          <div>
            <label className="label-text text-xs">Company / Group Name</label>
            <input
              type="text"
              value={branding.companyName || ''}
              onChange={e => updateBranding('companyName', e.target.value)}
              placeholder="e.g., Coury Hospitality"
              className="input-field text-sm py-2"
            />
          </div>

          {/* Show NEGroup badge */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={branding.showNEGLogo !== false}
              onChange={e => updateBranding('showNEGLogo', e.target.checked)}
              className="rounded border-neutral-600 bg-neutral-800 text-brand-gold focus:ring-brand-gold/30"
            />
            <span className="text-xs text-neutral-400">Show "Powered by Next Experience Group" in footer</span>
          </label>
        </div>
      )}

      {branding.source === 'negroup' && (
        <p className="text-[10px] text-neutral-600">Using default Next Experience Group branding. Switch to Company or Custom to add client branding.</p>
      )}

      {branding.source === 'company' && (
        <p className="text-[10px] text-neutral-600 mt-2">Company branding applies to all audits with the same company tag: <span className="text-brand-gold">{audit.intake?.companyTag || '(none set)'}</span></p>
      )}
    </div>
  );
}

// Helper to get branding for a given audit
export function getBranding(audit) {
  const b = audit?.branding || {};
  if (b.source === 'negroup' || !b.source) {
    return {
      logo: 'https://static.wixstatic.com/media/cf458d_f7329a8f179a4571a1951c4ed9431604~mv2.png',
      name: "What's NEXT",
      company: 'Next Experience Group',
      showPoweredBy: false,
    };
  }
  return {
    logo: b.clientLogo || 'https://static.wixstatic.com/media/cf458d_f7329a8f179a4571a1951c4ed9431604~mv2.png',
    name: b.clientName || audit?.intake?.conceptName || "What's NEXT",
    company: b.companyName || audit?.intake?.companyTag || '',
    showPoweredBy: b.showNEGLogo !== false,
  };
}
